import { FC, forwardRef, useRef } from 'react'
import { asPortal, useDeviceSize, useEventListener } from '@mettle/hooks'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import cx from 'classnames'
import cross from '@/assets/icons/crossWhite.svg'
import hamburger from '@/assets/icons/hamburger_white.svg'
import { AnimatedMenuLinks } from '@/components/Navigation/MenuLinks'
import { useBodySize } from '@/hooks/useBodySize'
import { isTabletSize } from '@/lib/device'
import styles from './styles.module.scss'
import { DynamicImage } from '@/components/DynamicImage'

interface MobileMenuProps {
  hideMenu(): void
  isMenuClosing: boolean
  isMenuVisible: boolean
}

export const MobileMenu: FC<MobileMenuProps> = ({
  hideMenu,
  isMenuClosing,
  isMenuVisible,
}) => {
  const bodySize = useBodySize()
  const tablet =
    isTabletSize(useDeviceSize()) ||
    (bodySize && bodySize.width >= 720 && bodySize.width <= 1080)

  const overlayRef = useRef(null)
  const menuLinksRef = useRef(null)

  return asPortal(
    <>
      <TransitionGroup component={null}>
        {tablet && !isMenuClosing && (
          <CSSTransition
            nodeRef={overlayRef}
            timeout={300}
            classNames={{
              appear: styles.fadeAppear,
              appearActive: styles.fadeAppearActive,
              enter: styles.fadeEnter,
              enterActive: styles.fadeEnterActive,
              exit: styles.fadeExit,
              exitActive: styles.fadeExitActive,
            }}
            appear
          >
            <Overlay ref={overlayRef} hideMenu={hideMenu} />
          </CSSTransition>
        )}
      </TransitionGroup>
      <TransitionGroup component={null}>
        {!isMenuClosing && (
          <CSSTransition
            nodeRef={menuLinksRef}
            timeout={300}
            classNames={
              tablet
                ? {
                    appear: styles.slideRightAppear,
                    appearActive: styles.slideRightAppearActive,
                    enter: styles.slideRightEnter,
                    enterActive: styles.slideRightEnterActive,
                    exit: styles.slideRightExit,
                    exitActive: styles.slideRightExitActive,
                  }
                : {
                    appear: styles.slideDownAppear,
                    appearActive: styles.slideDownAppearActive,
                    enter: styles.slideDownEnter,
                    enterActive: styles.slideDownEnterActive,
                    exit: styles.slideDownExit,
                    exitActive: styles.slideDownExitActive,
                  }
            }
            appear
          >
            <MobileMenuLinks
              ref={menuLinksRef}
              isMenuClosing={isMenuClosing}
              isMenuVisible={isMenuVisible}
              hideMenu={hideMenu}
            />
          </CSSTransition>
        )}
      </TransitionGroup>
    </>,
  )
}

export const MobileMenuLinks = forwardRef<HTMLElement, MobileMenuProps>(
  ({ isMenuClosing, hideMenu }, ref) => {
    const bounceRef = useRef<HTMLDivElement>(null)

    useEventListener(bounceRef.current, 'scroll', () => {
      const el = bounceRef.current!
      const isOverScroll =
        el.scrollTop < 0 || el.scrollTop > el.scrollHeight - el.clientHeight
      // @ts-expect-error - webkitOverflowScrolling is a valid style property on certain devices
      el.style.webkitOverflowScrolling = isOverScroll ? 'auto' : 'touch'
    })

    return (
      <section
        data-testid="mobile-menu-links"
        className={styles.mobileMenu}
        ref={(node: HTMLDivElement) => {
          bounceRef.current = node
          if (typeof ref === 'function') {
            ref(node)
          } else if (ref) {
            ref.current = node
          }
        }}
      >
        <div className={styles.wrapper}>
          <MobileMenuIcon isOpen toggle={hideMenu} className={styles.open} />
          <AnimatedMenuLinks
            className={styles.links}
            openClassName={styles.open}
            isMenuClosing={isMenuClosing}
            hideMenu={hideMenu}
          />
        </div>
      </section>
    )
  },
)

MobileMenuLinks.displayName = 'MobileMenuLinks'

export const MobileMenuIcon: FC<{
  isOpen: boolean
  toggle: () => void
  className?: string
}> = ({ isOpen, toggle, className }) => (
  <div className={className} onClick={toggle} role="button">
    {isOpen ? (
      <DynamicImage
        className={cx(styles.menuIcon, styles.closeIcon)}
        image={cross}
        alt="Close Menu"
        sizes="32px"
        local
      />
    ) : (
      <DynamicImage
        className={cx(styles.menuIcon, styles.openIcon)}
        image={hamburger}
        alt="Open Menu"
        sizes="32px"
        local
      />
    )}
  </div>
)

export const Overlay = forwardRef<HTMLDivElement, { hideMenu(): void }>(
  ({ hideMenu }, ref) => (
    <div
      ref={ref}
      data-testid="overlay"
      className={styles.overlay}
      onClick={hideMenu}
    />
  ),
)

Overlay.displayName = 'Overlay'
